// src/components/CinematicEngine.js
// Injects CSS overlays: vignette, scanlines, cursor glow, page transitions
// Excluded from /auth (login/register untouched)
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSound } from '../hooks/useSound';
import { useCinematic } from '../hooks/useCinematic';

const EXCLUDED_PATHS = ['/auth'];

let _styleInjected = false;

function injectStaticStyle() {
  if (_styleInjected) return;
  _styleInjected = true;

  const style = document.createElement('style');
  style.id = 'cin-global';
  style.textContent = `
    /* Vignette */
    #cin-vignette {
      position:fixed;inset:0;pointer-events:none;z-index:8985;
      background:radial-gradient(ellipse at 50% 50%, transparent 52%, rgba(0,0,0,0.6) 100%);
      transition:opacity 0.6s;
    }
    /* Scanlines */
    #cin-scan {
      position:fixed;inset:0;pointer-events:none;z-index:8986;
      background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.065) 2px,rgba(0,0,0,0.065) 4px);
      transition:opacity 0.6s;
    }
    /* Top neon bar */
    #cin-topbar {
      position:fixed;top:0;left:0;right:0;height:2px;pointer-events:none;z-index:8987;
      background:linear-gradient(90deg,transparent 0%,#00f5ff 30%,#7c3aed 70%,transparent 100%);
      animation:cinTopPulse 3.2s ease-in-out infinite;
      transition:opacity 0.6s;
    }
    @keyframes cinTopPulse { 0%,100%{opacity:0.45;filter:blur(1px)} 50%{opacity:1;filter:blur(2px)} }
    /* Cursor glow */
    #cin-cursor {
      position:fixed;width:26px;height:26px;border-radius:50%;pointer-events:none;z-index:8988;
      background:radial-gradient(circle,rgba(0,245,255,0.22) 0%,transparent 70%);
      transform:translate(-50%,-50%);
      mix-blend-mode:screen;
      transition:opacity 0.4s;
    }
    /* Page flash */
    #cin-flash {
      position:fixed;inset:0;pointer-events:none;z-index:9400;
      background:linear-gradient(135deg,rgba(0,245,255,0.08),rgba(124,58,237,0.08));
      opacity:0;transition:opacity 0.14s ease;
    }
    #cin-flash.active { opacity:1; }
    /* Ripple */
    .cin-ripple {
      position:absolute;border-radius:50%;pointer-events:none;
      background:rgba(0,245,255,0.28);
      transform:scale(0);animation:cinRipple 0.55s ease-out forwards;
    }
    @keyframes cinRipple { to{transform:scale(4);opacity:0;} }
  `;
  document.head.appendChild(style);

  // Vignette
  const vig = document.createElement('div'); vig.id = 'cin-vignette'; document.body.appendChild(vig);
  // Scanlines
  const scan = document.createElement('div'); scan.id = 'cin-scan'; document.body.appendChild(scan);
  // Top bar
  const tb = document.createElement('div'); tb.id = 'cin-topbar'; document.body.appendChild(tb);
  // Page flash
  const fl = document.createElement('div'); fl.id = 'cin-flash'; document.body.appendChild(fl);
  // Cursor
  const cur = document.createElement('div'); cur.id = 'cin-cursor'; document.body.appendChild(cur);
  window.addEventListener('mousemove', e => {
    cur.style.left = e.clientX + 'px';
    cur.style.top  = e.clientY + 'px';
  });

  // Global ripple on buttons
  window.addEventListener('click', e => {
    const btn = e.target.closest('button,[role="button"]');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 0.85;
    const rip = document.createElement('span');
    rip.className = 'cin-ripple';
    rip.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX-rect.left-size/2}px;top:${e.clientY-rect.top-size/2}px`;
    btn.style.overflow = 'hidden';
    btn.style.position = btn.style.position || 'relative';
    btn.appendChild(rip);
    setTimeout(() => rip.remove(), 600);
  });
}

export default function CinematicEngine() {
  const location = useLocation();
  const { playCinematicStart, startBgMusic } = useSound();
  const { glowFlash } = useCinematic();
  const startedRef = useRef(false);
  const bgRef = useRef(false);
  const prevPath = useRef(location.pathname);

  const excluded = EXCLUDED_PATHS.some(p => location.pathname.startsWith(p));

  // Inject overlays once
  useEffect(() => { injectStaticStyle(); }, []);

  // Toggle overlay visibility based on route
  useEffect(() => {
    const ids = ['cin-vignette','cin-scan','cin-topbar','cin-cursor'];
    const op = excluded ? '0' : '1';
    ids.forEach(id => { const el = document.getElementById(id); if (el) el.style.opacity = op; });
  }, [excluded]);

  // Page transition flash
  useEffect(() => {
    if (prevPath.current === location.pathname) return;
    prevPath.current = location.pathname;
    const fl = document.getElementById('cin-flash');
    if (!fl) return;
    fl.classList.add('active');
    setTimeout(() => fl.classList.remove('active'), 150);
    // Animate main
    const main = document.querySelector('.app-main');
    if (main) {
      main.classList.remove('page-enter');
      requestAnimationFrame(() => requestAnimationFrame(() => main.classList.add('page-enter')));
    }
  }, [location.pathname]);

  // Cinematic audio on first interaction
  useEffect(() => {
    if (excluded) return;
    const fire = () => {
      if (!startedRef.current) {
        startedRef.current = true;
        playCinematicStart();
        glowFlash('rgba(0,245,255,0.1)', 700);
        setTimeout(() => { if (!bgRef.current) { bgRef.current = true; startBgMusic(); } }, 3400);
      } else if (!bgRef.current) {
        bgRef.current = true;
        startBgMusic();
      }
      window.removeEventListener('click', fire);
      window.removeEventListener('keydown', fire);
    };
    window.addEventListener('click', fire);
    window.addEventListener('keydown', fire);
    return () => { window.removeEventListener('click', fire); window.removeEventListener('keydown', fire); };
  }, [excluded, playCinematicStart, startBgMusic, glowFlash]);

  return null;
}
